import type { ReactNode } from 'react'
import type { SkyBody } from '@/lib/astro/ephemeris'
import type { ConstellationFigure, StarCatalogue } from '@/lib/astro/catalogue'

import { useEffect, useMemo, useRef, useState } from 'react'
import { MoonPhase, Rotation_GAL_EQJ } from 'astronomy-engine'
import { cn } from '@/lib/utils'
import { registerSkyPointer } from '@/lib/sky-pointer'
import { loadConstellations, loadStars, starName } from '@/lib/astro/catalogue'
import { eqjToHorizon, vectorToRaDec } from '@/lib/astro/sky'
import { moonLitPath } from '@/components/generals/moon-glyph'

interface SkyChartProps {
    time: Date
    bodies: SkyBody[]
    selected?: string | null
    onSelect?: (key: string) => void
    className?: string
    label?: string
}

type Hover = {
    key: string
    title: string
    detail: string
    x: number
    y: number
    selectable: boolean
}

type Point = { x: number, y: number }

const BODY_COLOURS: Record<string, string> = {
    mercury: '#d9c3b0',
    venus: '#fff4dc',
    mars: '#f08a5d',
    jupiter: '#f3dfc0',
    saturn: '#ead29a',
    uranus: '#b9e6ee',
    neptune: '#8fb3ff'
}

const COMPASS_LABELS = [
    { label: 'N', az: 0 },
    { label: 'NE', az: 45 },
    { label: 'E', az: 90 },
    { label: 'SE', az: 135 },
    { label: 'S', az: 180 },
    { label: 'SW', az: 225 },
    { label: 'W', az: 270 },
    { label: 'NW', az: 315 }
]

const createRandom = (seed: number) => {
    let state = seed

    return () => {
        state = (state * 1664525 + 1013904223) >>> 0

        return state / 4294967296
    }
}

/** Points scattered along the galactic plane, brighter toward Sagittarius, in J2000 vectors. */
const buildMilkyWay = (): Float32Array => {
    const random = createRandom(3141)
    const { rot } = Rotation_GAL_EQJ()
    const count = 2600
    const out = new Float32Array(count * 4)

    for (let i = 0; i < count; i++) {
        const l = random() * Math.PI * 2
        const u1 = Math.max(1e-6, random())
        const gauss = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * random())
        const b = gauss * (0.07 + 0.05 * Math.cos(l) ** 2)
        const gx = Math.cos(b) * Math.cos(l)
        const gy = Math.cos(b) * Math.sin(l)
        const gz = Math.sin(b)

        out[i * 4] = rot[0][0] * gx + rot[1][0] * gy + rot[2][0] * gz
        out[i * 4 + 1] = rot[0][1] * gx + rot[1][1] * gy + rot[2][1] * gz
        out[i * 4 + 2] = rot[0][2] * gx + rot[1][2] * gy + rot[2][2] * gz
        out[i * 4 + 3] = 0.35 + 0.65 * Math.pow(Math.cos(l / 2), 4)
    }

    return out
}

let spriteCache: HTMLCanvasElement | null = null

const glowSprite = (): HTMLCanvasElement => {
    if (spriteCache) return spriteCache

    const canvas = document.createElement('canvas')

    canvas.width = 64
    canvas.height = 64

    const context = canvas.getContext('2d')

    if (context) {
        const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32)

        gradient.addColorStop(0, 'rgba(176, 186, 214, 1)')
        gradient.addColorStop(0.5, 'rgba(176, 186, 214, 0.35)')
        gradient.addColorStop(1, 'rgba(176, 186, 214, 0)')
        context.fillStyle = gradient
        context.fillRect(0, 0, 64, 64)
    }

    spriteCache = canvas

    return canvas
}

const ECLIPTIC = (() => {
    const obliquity = (23.4393 * Math.PI) / 180
    const points: [number, number, number][] = []

    for (let degree = 0; degree <= 360; degree += 3) {
        const l = (degree * Math.PI) / 180

        points.push([Math.cos(l), Math.sin(l) * Math.cos(obliquity), Math.sin(l) * Math.sin(obliquity)])
    }

    return points
})()

/** A circular chart of the whole sky above the observatory, zenith in the middle, east on the left. */
export const SkyChart = ({
    time,
    bodies,
    selected,
    onSelect,
    className,
    label = 'Chart of the sky above the observatory'
}: SkyChartProps): ReactNode => {
    const wrapRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [catalogue, setCatalogue] = useState<StarCatalogue | null>(null)
    const [figures, setFigures] = useState<ConstellationFigure[]>([])
    const [size, setSize] = useState(0)
    const [hover, setHover] = useState<Hover | null>(null)
    const [fonts, setFonts] = useState({ mono: 'monospace', serif: 'serif' })
    const milkyWay = useMemo(() => (typeof window === 'undefined' ? null : buildMilkyWay()), [])
    const projectedBodies = useRef<{ key: string, point: Point, body: SkyBody }[]>([])
    const projectedStars = useRef<{ name: string, mag: number, point: Point }[]>([])
    const matrixRef = useRef<Float64Array | null>(null)

    useEffect(() => {
        let alive = true

        loadStars().then(value => {
            if (alive) setCatalogue(value)
        }).catch(() => undefined)
        loadConstellations().then(value => {
            if (alive) setFigures(value)
        }).catch(() => undefined)

        const styles = getComputedStyle(document.documentElement)

        setFonts({
            mono: styles.getPropertyValue('--font-app-mono').trim() || 'monospace',
            serif: styles.getPropertyValue('--font-app-heading').trim() || 'serif'
        })

        return () => {
            alive = false
        }
    }, [])

    useEffect(() => {
        const wrap = wrapRef.current

        if (!wrap) return

        const observer = new ResizeObserver(([entry]) => {
            setSize(Math.round(entry.contentRect.width))
        })

        observer.observe(wrap)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context || size < 10) return

        const ratio = Math.min(window.devicePixelRatio || 1, 2)

        canvas.width = Math.round(size * ratio)
        canvas.height = Math.round(size * ratio)
        context.setTransform(ratio, 0, 0, ratio, 0, 0)

        const cx = size / 2
        const cy = size / 2
        const R = size / 2 - Math.max(26, size * 0.06)
        const m = eqjToHorizon(time)

        matrixRef.current = m

        const project = (x: number, y: number, z: number): Point | null => {
            const hx = m[0] * x + m[1] * y + m[2] * z
            const hy = m[3] * x + m[4] * y + m[5] * z
            const hz = m[6] * x + m[7] * y + m[8] * z

            if (hz < -0.005) return null

            const zenithAngle = Math.acos(Math.max(-1, Math.min(1, hz)))
            const r = R * Math.tan(zenithAngle / 2)
            const azimuth = Math.atan2(-hy, hx)

            return { x: cx - r * Math.sin(azimuth), y: cy - r * Math.cos(azimuth) }
        }

        const polar = (azimuthDeg: number, r: number): Point => {
            const a = (azimuthDeg * Math.PI) / 180

            return { x: cx - r * Math.sin(a), y: cy - r * Math.cos(a) }
        }

        context.clearRect(0, 0, size, size)

        const sky = context.createRadialGradient(cx, cy, 0, cx, cy, R)

        sky.addColorStop(0, '#05070d')
        sky.addColorStop(0.75, '#070b15')
        sky.addColorStop(1, '#0d1628')
        context.fillStyle = sky
        context.beginPath()
        context.arc(cx, cy, R, 0, Math.PI * 2)
        context.fill()

        context.save()
        context.beginPath()
        context.arc(cx, cy, R, 0, Math.PI * 2)
        context.clip()

        if (milkyWay) {
            const sprite = glowSprite()
            const spread = size * 0.024

            context.globalCompositeOperation = 'lighter'

            for (let i = 0; i < milkyWay.length; i += 4) {
                const point = project(milkyWay[i], milkyWay[i + 1], milkyWay[i + 2])

                if (!point) continue

                context.globalAlpha = 0.05 * milkyWay[i + 3]
                context.drawImage(sprite, point.x - spread, point.y - spread, spread * 2, spread * 2)
            }

            context.globalAlpha = 1
            context.globalCompositeOperation = 'source-over'
        }

        context.strokeStyle = 'rgba(237, 230, 216, 0.09)'
        context.lineWidth = 1
        context.setLineDash([2, 5])

        for (const altitude of [30, 60]) {
            const r = R * Math.tan(((90 - altitude) * Math.PI) / 360)

            context.beginPath()
            context.arc(cx, cy, r, 0, Math.PI * 2)
            context.stroke()
        }

        context.setLineDash([])

        for (let az = 0; az < 360; az += 45) {
            const end = polar(az, R)

            context.beginPath()
            context.moveTo(cx, cy)
            context.lineTo(end.x, end.y)
            context.stroke()
        }

        context.strokeStyle = 'rgba(198, 167, 110, 0.32)'
        context.setLineDash([4, 6])
        context.beginPath()

        let penDown = false

        for (const point of ECLIPTIC) {
            const p = project(point[0], point[1], point[2])

            if (!p) {
                penDown = false
                continue
            }

            if (!penDown) context.moveTo(p.x, p.y)
            else context.lineTo(p.x, p.y)
            penDown = true
        }

        context.stroke()
        context.setLineDash([])

        context.strokeStyle = 'rgba(198, 167, 110, 0.26)'
        context.lineWidth = 0.8
        context.font = `${Math.max(8, Math.round(size * 0.0135))}px ${fonts.mono}`
        context.textAlign = 'center'

        for (const figure of figures) {
            for (const line of figure.lines) {
                context.beginPath()

                let started = false

                for (const vertex of line) {
                    const p = project(vertex[0], vertex[1], vertex[2])

                    if (!p) {
                        started = false
                        continue
                    }

                    if (!started) context.moveTo(p.x, p.y)
                    else context.lineTo(p.x, p.y)
                    started = true
                }

                context.stroke()
            }

            if (figure.rank <= 2) {
                const anchor = project(figure.anchor[0], figure.anchor[1], figure.anchor[2])

                if (anchor) {
                    context.fillStyle = 'rgba(189, 181, 166, 0.5)'
                    context.fillText(figure.name.toUpperCase(), anchor.x, anchor.y)
                }
            }
        }

        const named: { name: string, mag: number, point: Point }[] = []

        if (catalogue) {
            for (let i = 0; i < catalogue.count; i++) {
                const mag = catalogue.mag[i]

                if (mag > 5.4) break

                const p = project(catalogue.xyz[i * 3], catalogue.xyz[i * 3 + 1], catalogue.xyz[i * 3 + 2])

                if (!p) continue

                const radius = Math.max(0.45, (5.7 - mag) * 0.58) * (size / 640)
                const r = Math.round(catalogue.rgb[i * 3] * 255)
                const g = Math.round(catalogue.rgb[i * 3 + 1] * 255)
                const b = Math.round(catalogue.rgb[i * 3 + 2] * 255)
                const alpha = Math.min(1, 0.35 + (5.6 - mag) * 0.16)

                if (mag < 1.6) {
                    const halo = context.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 4)

                    halo.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.28)`)
                    halo.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
                    context.fillStyle = halo
                    context.beginPath()
                    context.arc(p.x, p.y, radius * 4, 0, Math.PI * 2)
                    context.fill()
                }

                context.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
                context.beginPath()
                context.arc(p.x, p.y, radius, 0, Math.PI * 2)
                context.fill()

                const name = starName(catalogue.hip[i])

                if (name) named.push({ name, mag, point: p })
            }
        }

        projectedStars.current = named

        context.font = `italic ${Math.max(10, Math.round(size * 0.019))}px ${fonts.serif}`
        context.textAlign = 'left'
        context.fillStyle = 'rgba(237, 230, 216, 0.62)'

        for (const star of named) {
            if (star.mag < 1.3) context.fillText(star.name, star.point.x + 7, star.point.y - 6)
        }

        const placed: { key: string, point: Point, body: SkyBody }[] = []

        for (const body of bodies) {
            if (body.kind === 'sun') continue

            const p = project(body.vector[0], body.vector[1], body.vector[2])

            if (!p) continue

            placed.push({ key: body.key, point: p, body })

            const isSelected = selected === body.key

            if (body.kind === 'moon') {
                const radius = Math.max(7, size * 0.016)
                const waxing = MoonPhase(time) < 180

                context.save()
                context.translate(p.x, p.y)
                context.fillStyle = '#1b2232'
                context.beginPath()
                context.arc(0, 0, radius, 0, Math.PI * 2)
                context.fill()
                context.fillStyle = '#f1ead9'
                context.fill(new Path2D(moonLitPath(body.phaseFraction, waxing, radius)))
                context.restore()
            }
            else {
                const radius = Math.max(2.2, (2.6 - body.magnitude) * 1.15) * (size / 640)

                context.fillStyle = BODY_COLOURS[body.key] ?? '#ffffff'
                context.beginPath()
                context.arc(p.x, p.y, radius, 0, Math.PI * 2)
                context.fill()
            }

            context.strokeStyle = isSelected ? '#c6a76e' : 'rgba(198, 167, 110, 0.55)'
            context.lineWidth = isSelected ? 1.4 : 1
            context.beginPath()
            context.arc(p.x, p.y, body.kind === 'moon' ? Math.max(12, size * 0.024) : Math.max(8, size * 0.016), 0, Math.PI * 2)
            context.stroke()

            context.fillStyle = '#c6a76e'
            context.font = `${Math.max(9, Math.round(size * 0.015))}px ${fonts.mono}`
            context.fillText(body.name.toUpperCase(), p.x + Math.max(12, size * 0.022), p.y + 4)
        }

        projectedBodies.current = placed

        context.restore()

        context.strokeStyle = 'rgba(198, 167, 110, 0.85)'
        context.lineWidth = 1
        context.beginPath()
        context.arc(cx, cy, R, 0, Math.PI * 2)
        context.stroke()

        context.strokeStyle = 'rgba(237, 230, 216, 0.28)'

        for (let az = 0; az < 360; az += 5) {
            const major = az % 15 === 0
            const inner = polar(az, R + 3)
            const outer = polar(az, R + (major ? 10 : 6))

            context.beginPath()
            context.moveTo(inner.x, inner.y)
            context.lineTo(outer.x, outer.y)
            context.stroke()
        }

        context.textAlign = 'center'
        context.textBaseline = 'middle'

        for (const { label: text, az } of COMPASS_LABELS) {
            const position = polar(az, R + Math.max(18, size * 0.035))
            const cardinal = text.length === 1

            context.fillStyle = cardinal ? '#ede6d8' : 'rgba(189, 181, 166, 0.6)'
            context.font = `${cardinal ? Math.max(11, Math.round(size * 0.02)) : Math.max(8, Math.round(size * 0.014))}px ${fonts.mono}`
            context.fillText(text, position.x, position.y)
        }

        context.textBaseline = 'alphabetic'
        context.strokeStyle = 'rgba(237, 230, 216, 0.35)'
        context.beginPath()
        context.moveTo(cx - 5, cy)
        context.lineTo(cx + 5, cy)
        context.moveTo(cx, cy - 5)
        context.lineTo(cx, cy + 5)
        context.stroke()
    }, [time, bodies, catalogue, figures, size, selected, fonts, milkyWay])

    const pickAt = (clientX: number, clientY: number): Hover | null => {
        const canvas = canvasRef.current

        if (!canvas) return null

        const rect = canvas.getBoundingClientRect()
        const x = clientX - rect.left
        const y = clientY - rect.top

        let best: Hover | null = null
        let bestDistance = Math.max(16, size * 0.03)

        for (const item of projectedBodies.current) {
            const distance = Math.hypot(item.point.x - x, item.point.y - y)

            if (distance < bestDistance) {
                bestDistance = distance
                best = {
                    key: item.key,
                    title: item.body.name,
                    detail: `${item.body.kind === 'moon' ? `${Math.round(item.body.phaseFraction * 100)}% lit` : `Magnitude ${item.body.magnitude.toFixed(1)}`} · ${item.body.constellation}`,
                    x: item.point.x,
                    y: item.point.y,
                    selectable: true
                }
            }
        }

        if (best) return best

        bestDistance = 10

        for (const star of projectedStars.current) {
            const distance = Math.hypot(star.point.x - x, star.point.y - y)

            if (distance < bestDistance) {
                bestDistance = distance
                best = {
                    key: `star-${star.name}`,
                    title: star.name,
                    detail: `Star · magnitude ${star.mag.toFixed(1)}`,
                    x: star.point.x,
                    y: star.point.y,
                    selectable: false
                }
            }
        }

        return best
    }

    useEffect(() => {
        const wrap = wrapRef.current

        if (!wrap) return

        return registerSkyPointer(wrap, (clientX, clientY) => {
            const m = matrixRef.current
            const canvas = canvasRef.current

            if (!m || !canvas || size < 10) return null

            const rect = canvas.getBoundingClientRect()
            const cx = rect.width / 2
            const cy = rect.height / 2
            const R = rect.width / 2 - Math.max(26, rect.width * 0.06)
            const dx = cx - (clientX - rect.left)
            const dy = cy - (clientY - rect.top)
            const r = Math.hypot(dx, dy)

            if (r > R) return null

            const azimuth = Math.atan2(dx, dy)
            const altitude = Math.PI / 2 - 2 * Math.atan(r / R)
            const hx = Math.cos(altitude) * Math.cos(azimuth)
            const hy = -Math.cos(altitude) * Math.sin(azimuth)
            const hz = Math.sin(altitude)
            const x = m[0] * hx + m[3] * hy + m[6] * hz
            const y = m[1] * hx + m[4] * hy + m[7] * hz
            const z = m[2] * hx + m[5] * hy + m[8] * hz

            return vectorToRaDec(x, y, z)
        })
    }, [size])

    return (
        <div ref={wrapRef} data-sky className={cn('relative aspect-square w-full', className)}>
            <div className='media absolute inset-0 rounded-full'>
                <canvas
                    ref={canvasRef}
                    role='img'
                    aria-label={label}
                    className='block h-full w-full'
                    data-cursor={hover?.selectable ? 'link' : undefined}
                    onPointerMove={event => setHover(pickAt(event.clientX, event.clientY))}
                    onPointerLeave={() => setHover(null)}
                    onClick={event => {
                        const hit = pickAt(event.clientX, event.clientY)

                        if (hit?.selectable) onSelect?.(hit.key)
                    }}
                    style={{ cursor: hover?.selectable ? 'pointer' : 'crosshair' }}
                />
            </div>

            {hover && (
                <div
                    className={cn(
                        'pointer-events-none absolute z-10 -translate-y-[calc(100%+18px)] whitespace-nowrap border border-hair bg-ink-2/95 px-3 py-2 text-center backdrop-blur',
                        hover.x < size * 0.3 ? '-translate-x-6' : hover.x > size * 0.7 ? 'translate-x-[calc(-100%+1.5rem)]' : '-translate-x-1/2'
                    )}
                    style={{ left: hover.x, top: hover.y }}
                >
                    <p className='font-heading text-lg leading-tight text-star'>
                        {hover.title}
                    </p>
                    <p className='label-mono mt-0.5 text-[0.62rem] text-star-3'>
                        {hover.detail}
                    </p>
                    {hover.selectable && (
                        <p className='label-mono mt-1 text-[0.62rem] text-brass'>
                            Click for detail
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}
